import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import {
    Chart,
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
} from "chart.js";

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip
);

export default function ScanHorizontalBarChart({
    title,
    subtitle,
    data,
    onBarClick
}) {
    const { t } = useTranslation();

    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current) return;

        chartRef.current?.destroy();

        chartRef.current = new Chart(canvasRef.current, {
            type: "bar",

            data: {
                labels: data.map((item) => item.name),
                datasets: [
                    {
                        data: data.map((item) => item.value),
                        backgroundColor: data.map(
                            (item) => item.color || "#5b10f5"
                        ),
                        borderWidth: 0,
                        borderRadius: 5,
                        barThickness: 8,
                    },
                ],
            },

            options: {
                indexAxis: "y",
                responsive: true,
                maintainAspectRatio: false,

                onClick: (event, elements) => {
                    if (elements && elements.length > 0 && onBarClick) {
                        const dataIndex = elements[0].index;
                        const clickedItem = data[dataIndex];
                        onBarClick(clickedItem);
                    }
                },

                onHover: (event, elements) => {
                    if (event.native && event.native.target) {
                        event.native.target.style.cursor = elements && elements.length > 0 ? 'pointer' : 'default';
                    }
                },

                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) =>
                                `${context.raw} finding${
                                    context.raw > 1 ? "s" : ""
                                }`,
                        },
                    },
                },

                scales: {
                    x: {
                        beginAtZero: true,
                        grid: {
                            color: "#eef0f7",
                            drawBorder: false,
                        },
                        border: {
                            display: false,
                        },
                        ticks: {
                            precision: 0,
                            font: {
                                size: 10,
                            },
                            color: "#8a8fa3",
                        },
                    },

                    y: {
                        grid: {
                            display: false,
                        },
                        border: {
                            display: false,
                        },
                        ticks: {
                            color: "#6f7485",
                            font: {
                                size: 9,
                            },
                            callback(value) {
                                const label = this.getLabelForValue(value);
                                if (label.length > 34) {
                                    return `${label.slice(0, 34)}…`;
                                }
                                return label;
                            },
                        },
                    },
                },
            },
        });

        return () => {
            chartRef.current?.destroy();
        };
    }, [data]);

    return (
        <div className="scan-charts-content">
            <div className="scan-charts-content-header">
                <div className="scan-charts-content-header-label">
                    <h3 className="scan-charts-content-header-label-title">
                        {t(title)}
                    </h3>

                    {subtitle && (
                        <p className="scan-charts-content-header-label-subtitle">
                            {t(subtitle)}
                        </p>
                    )}
                </div>
            </div>

            <div className="scan-horizontal-bar-chart">
                <canvas ref={canvasRef} />
            </div>
        </div>
    );
}